import * as React from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────
// Design system du brief « CATHO SCHOOL — version web paiements »
//
// Tokens littéraux :
//   cream  #FDFBF7  fond page
//   card   #FFFFFF
//   navy   #1B2A5E (primary)
//   navyDark #13204A
//   gold   #D4A038 (accent)
//   goldSoft #E6B547
//   graphite #1C1C1C (texte uniquement, jamais en surface)
//   muted  #6B6B6B
//
// Règles strictes : tout texte sur fond navy ou or filled = blanc #FFFFFF,
// vouvoiement, pas d'emoji, pas de tiret cadratin, pas de noir/graphite en
// surface. Tokens passés en valeurs arbitraires Tailwind pour fidélité au
// pixel sans modifier la config globale.
// ─────────────────────────────────────────────────────────────────────────

// ─── Bouton ──────────────────────────────────────────────────────────────
type BoutonVariant = "primary" | "gold" | "outline" | "ghost" | "destructive";
type BoutonSize = "md" | "lg";

interface BoutonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BoutonVariant;
  size?: BoutonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<BoutonVariant, string> = {
  primary:
    "bg-[#1B2A5E] text-white hover:bg-[#13204A] hover:-translate-y-px hover:shadow-[0_10px_18px_rgba(19,32,74,0.22)] active:scale-[0.98]",
  gold:
    "bg-[#E6B547] text-[#1C1C1C] hover:bg-[#D4A038] hover:-translate-y-px hover:shadow-[0_10px_18px_rgba(212,160,56,0.28)] active:scale-[0.98]",
  outline:
    "border-[1.5px] border-[#1B2A5E] bg-transparent text-[#1B2A5E] hover:bg-[#1B2A5E]/[0.06] active:scale-[0.98]",
  ghost:
    "bg-transparent text-[#1B2A5E] hover:bg-[#1B2A5E]/[0.06] active:scale-[0.98]",
  destructive:
    "bg-[#C84B3F] text-white hover:bg-[#B23A2F] active:scale-[0.98]",
};

const SIZE_CLASSES: Record<BoutonSize, string> = {
  md: "h-[52px] px-5 text-[15px]",
  lg: "h-[56px] px-6 text-[16px]",
};

export const Bouton = React.forwardRef<HTMLButtonElement, BoutonProps>(
  function Bouton(
    { variant = "primary", size = "md", loading, fullWidth, className, children, disabled, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-[14px] font-semibold transition-all duration-150",
          "[font-family:'Inter',sans-serif]",
          "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#E6B547]/35",
          "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none",
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {loading ? (
          <Spinner
            size={20}
            tone={variant === "primary" || variant === "destructive" ? "white" : "navy"}
          />
        ) : children}
      </button>
    );
  },
);

// ─── Spinner ─────────────────────────────────────────────────────────────
export function Spinner({
  size = 24,
  tone = "navy",
}: {
  size?: number;
  tone?: "navy" | "white" | "gold";
}) {
  const color = tone === "white" ? "#FFFFFF" : tone === "gold" ? "#E6B547" : "#1B2A5E";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-label="Chargement en cours"
      role="status"
    >
      <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.18" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Champ (input) ───────────────────────────────────────────────────────
interface ChampProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  rightAdornment?: React.ReactNode;
  leftAdornment?: React.ReactNode;
}

export const Champ = React.forwardRef<HTMLInputElement, ChampProps>(function Champ(
  { label, hint, error, required, rightAdornment, leftAdornment, className, id, ...rest },
  ref,
) {
  const autoId = React.useId();
  const inputId = id ?? autoId;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="text-[13px] font-medium text-[#13204A] [font-family:'Inter',sans-serif]"
        >
          {label}
          {required && <span className="text-[#C84B3F] ml-0.5">*</span>}
        </label>
      )}
      <div
        className={cn(
          "relative flex items-center bg-white rounded-[14px] border h-[52px] transition-all",
          error
            ? "border-[#C84B3F]"
            : "border-[#E5E5E5] focus-within:border-[#E6B547] focus-within:shadow-[0_0_0_3px_rgba(230,181,71,0.20)]",
        )}
      >
        {leftAdornment && (
          <div className="pl-4 text-[#6B6B6B] flex items-center">{leftAdornment}</div>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${inputId}-msg` : undefined}
          className={cn(
            "flex-1 bg-transparent outline-none px-4 text-[15px] text-[#1C1C1C] placeholder:text-[#6B6B6B]",
            "[font-family:'Inter',sans-serif]",
            "disabled:cursor-not-allowed disabled:opacity-60",
            className,
          )}
          {...rest}
        />
        {rightAdornment && <div className="pr-4 text-[#6B6B6B] flex items-center">{rightAdornment}</div>}
      </div>
      {(error || hint) && (
        <p
          id={`${inputId}-msg`}
          className={cn(
            "text-[12px] [font-family:'Inter',sans-serif]",
            error ? "text-[#C84B3F]" : "text-[#6B6B6B]",
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

// ─── Carte standard ──────────────────────────────────────────────────────
export function Carte({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "bg-white border border-[#E5E5E5] rounded-[18px] p-5",
        "shadow-[0_6px_16px_rgba(27,42,94,0.06)]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── SolidCard (hero navy ou or) ─────────────────────────────────────────
export function SolidCard({
  className,
  tone = "navy",
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { tone?: "navy" | "gold" }) {
  const bg = tone === "navy" ? "bg-[#1B2A5E]" : "bg-[#E6B547]";
  const shadow =
    tone === "navy"
      ? "shadow-[0_10px_18px_rgba(19,32,74,0.22)]"
      : "shadow-[0_10px_18px_rgba(212,160,56,0.22)]";
  return (
    <div
      className={cn("rounded-[22px] p-6 text-white", bg, shadow, className)}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── GlassCard ───────────────────────────────────────────────────────────
export function GlassCard({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[22px] p-6 border border-white/70",
        "bg-white/55 backdrop-blur-[24px] [backdrop-filter:blur(24px)_saturate(140%)]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── Eyebrow ─────────────────────────────────────────────────────────────
export function Eyebrow({
  children,
  tone = "gold",
  className,
}: {
  children: React.ReactNode;
  tone?: "gold" | "white";
  className?: string;
}) {
  return (
    <p
      className={cn(
        "[font-family:'Outfit',sans-serif] font-semibold uppercase",
        "text-[10px] tracking-[1.6px]",
        // #B8881F (gold700) sur cream #FDFBF7 = 4.74:1 (AA small text OK).
        // /90 : seule graduation générée par Tailwind 3.4 (92 hors échelle).
        tone === "white" ? "text-white/90" : "text-[#B8881F]",
        className,
      )}
    >
      {children}
    </p>
  );
}

// ─── Pastille (Badge) ────────────────────────────────────────────────────
type PastilleVariant = "success" | "warning" | "error" | "info" | "neutral";

const PASTILLE_CLASSES: Record<PastilleVariant, string> = {
  success: "bg-[#E4F1E8] text-[#3F8F5C]",
  warning: "bg-[#FBF1D6] text-[#B8881F]",
  error: "bg-[#F8E2DF] text-[#C84B3F]",
  info: "bg-[#E2EAF5] text-[#3D6BB8]",
  neutral: "bg-[#F1F3FA] text-[#243776]",
};

export function Pastille({
  children,
  variant = "neutral",
  className,
}: {
  children: React.ReactNode;
  variant?: PastilleVariant;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium",
        "[font-family:'Inter',sans-serif] whitespace-nowrap",
        PASTILLE_CLASSES[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── Stepper ─────────────────────────────────────────────────────────────
export interface StepperStep {
  label: string;
}

export function Stepper({
  steps,
  current,
  className,
}: {
  steps: StepperStep[];
  /** 1-indexed active step */
  current: number;
  className?: string;
}) {
  return (
    <ol
      className={cn("flex items-start gap-2 w-full", className)}
      aria-label="Étapes du paiement"
    >
      {steps.map((s, i) => {
        const idx = i + 1;
        const isDone = idx < current;
        const isActive = idx === current;
        const isFuture = idx > current;
        return (
          <li key={s.label} className="flex-1 flex flex-col items-center gap-2 min-w-0">
            <div className="flex items-center w-full">
              <div className={cn("flex-1 h-px", i === 0 ? "opacity-0" : isDone || isActive ? "bg-[#E6B547]" : "bg-[#E2E7F4]")} />
              <div
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0",
                  "[font-family:'Inter',sans-serif]",
                  isActive && "bg-[#1B2A5E] text-white",
                  isDone && "bg-[#E6B547] text-white",
                  isFuture && "bg-[#E2E7F4] text-[#6B83D6]",
                )}
              >
                {isDone ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  idx
                )}
              </div>
              <div className={cn("flex-1 h-px", i === steps.length - 1 ? "opacity-0" : isDone ? "bg-[#E6B547]" : "bg-[#E2E7F4]")} />
            </div>
            <span
              className={cn(
                "text-[12px] font-semibold text-center [font-family:'Inter',sans-serif] leading-tight",
                isActive ? "text-[#1B2A5E]" : "text-[#6B6B6B]",
              )}
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
